import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CircleCheck, CircleX, CircleAlert, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

interface KaycooStatus {
  connected: boolean;
  authenticated: boolean | null;
  agent_count: number;
  latency_ms: number | null;
  checked_at: string | null;
}

interface KaycooStatusResponse {
  connected: boolean;
  authenticated: boolean | null;
  agent_count: number;
  latency_ms: number | null;
  checked_at: string;
}

type StatusTone = 'ok' | 'warn' | 'error';

function describeStatus(s: KaycooStatus): { label: string; tone: StatusTone } {
  if (!s.connected) return { label: 'Disconnected', tone: 'error' };
  if (s.authenticated === false) {
    return { label: 'Auth failed — API key invalid for this app', tone: 'error' };
  }
  if (s.authenticated === null) {
    return { label: 'Reachable — auth status unknown', tone: 'warn' };
  }
  if (s.agent_count === 0) {
    return { label: 'Connected — 0 agents bound to this app', tone: 'warn' };
  }
  return { label: `Connected — ${s.agent_count} agents`, tone: 'ok' };
}

interface KaycooConfigCardProps {
  isLoading: boolean;
  className?: string;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export function KaycooConfigCard({ isLoading, className }: KaycooConfigCardProps) {
  const [status, setStatus] = useState<KaycooStatus | null>(null);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async (notify: boolean) => {
    setTesting(true);
    try {
      const result = await api.get<KaycooStatusResponse>('/api/v1/settings/kaycoo/health');
      const next: KaycooStatus = {
        connected: result.connected,
        authenticated: result.authenticated,
        agent_count: result.agent_count,
        latency_ms: result.latency_ms,
        checked_at: result.checked_at,
      };
      setStatus(next);
      if (notify) {
        const view = describeStatus(next);
        if (view.tone === 'ok') toast.success(view.label);
        else if (view.tone === 'warn') toast.warning(view.label);
        else toast.error(view.label);
      }
    } catch {
      setStatus({
        connected: false,
        authenticated: null,
        agent_count: 0,
        latency_ms: null,
        checked_at: new Date().toISOString(),
      });
      if (notify) {
        toast.error('Unable to reach Kaycoo AI platform. Compliance Agent will not function correctly.');
      }
    } finally {
      setTesting(false);
    }
  }, []);

  // Auto-fetch on mount so the user sees current status without clicking.
  useEffect(() => { void refresh(false); }, [refresh]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-9 w-36" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>AI Platform Connection</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Kaycoo Agentic Platform (<code className="font-mono text-xs">api.kaycoo.ai</code>) — read-only.
          Platform configuration is managed directly on the Kaycoo platform.
        </p>

        {status && (() => {
          const view = describeStatus(status);
          const toneClass =
            view.tone === 'ok' ? 'text-green-700'
            : view.tone === 'warn' ? 'text-amber-700 dark:text-amber-400'
            : 'text-destructive';
          const Icon = view.tone === 'ok' ? CircleCheck : view.tone === 'warn' ? CircleAlert : CircleX;
          const iconClass =
            view.tone === 'ok' ? 'text-green-600'
            : view.tone === 'warn' ? 'text-amber-600'
            : 'text-destructive';
          return (
            <dl className="space-y-2 text-sm" aria-label="Kaycoo connection status">
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground w-28 shrink-0">Status</dt>
                <dd className="flex items-center gap-1.5 font-medium">
                  <Icon className={`h-4 w-4 ${iconClass}`} aria-hidden="true" />
                  <span className={toneClass}>{view.label}</span>
                </dd>
              </div>
              {status.latency_ms !== null && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground w-28 shrink-0">Latency</dt>
                  <dd>{status.latency_ms}ms</dd>
                </div>
              )}
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground w-28 shrink-0">Last checked</dt>
                <dd>{formatTimestamp(status.checked_at)}</dd>
              </div>
            </dl>
          );
        })()}

        <Button
          variant="outline"
          size="sm"
          onClick={() => { void refresh(true); }}
          disabled={testing}
          aria-busy={testing}
        >
          {testing
            ? <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />
            : <RefreshCw className="h-4 w-4 mr-1" aria-hidden="true" />}
          {testing ? 'Testing…' : 'Refresh'}
        </Button>
      </CardContent>
    </Card>
  );
}
